import { buildMirrorUrls, MIRROR_PROBE_TIMEOUT_MS } from '../config/mirrors';
import { isSiteAvailable } from './siteStatus';

/**
 * @param {Response} response
 * @returns {Promise<boolean>}
 */
async function isApiStatusOk(response) {
    if (!response.ok) {
        return false;
    }

    try {
        const data = await response.json();
        return isSiteAvailable(data);
    } catch {
        return false;
    }
}

/**
 * @param {string} url
 * @param {RequestInit} init
 * @param {number} timeoutMs
 * @returns {Promise<Response>}
 */
function fetchWithTimeout(url, init, timeoutMs) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    if (init.signal) {
        init.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    return fetch(url, { ...init, signal: controller.signal }).finally(() => {
        clearTimeout(timeoutId);
    });
}

/**
 * @param {ReturnType<typeof buildMirrorUrls>} mirror
 * @param {{ signal?: AbortSignal, timeoutMs?: number }} [options]
 * @returns {Promise<ReturnType<typeof buildMirrorUrls> | null>}
 */
export async function probeMirror(mirror, options = {}) {
    const timeoutMs = options.timeoutMs ?? MIRROR_PROBE_TIMEOUT_MS;
    const init = {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: options.signal,
    };

    try {
        const response = await fetchWithTimeout(mirror.statusUrl, init, timeoutMs);
        const apiOk = await isApiStatusOk(response);
        return apiOk ? mirror : null;
    } catch {
        return null;
    }
}

/**
 * @param {string[]} domains
 * @param {{ signal?: AbortSignal, timeoutMs?: number }} [options]
 * @returns {Promise<ReturnType<typeof buildMirrorUrls>[]>}
 */
export async function findAvailableMirrors(domains, options = {}) {
    const mirrors = domains.map(buildMirrorUrls);
    const results = await Promise.all(
        mirrors.map((mirror) => probeMirror(mirror, options)),
    );

    return results.filter(Boolean);
}
